/**
 * Auth for this console, kept independent of `app/src/lib/auth.tsx` on
 * purpose. That file is a React context with a passkey ceremony behind it,
 * and importing it here would mean two node_modules trees each shipping
 * their own copy of React in one page — the "invalid hook call" a shared
 * component runs into the moment either copy touches a hook. Everything
 * below is plain `@supabase/supabase-js`, which has no such problem: the
 * Supabase client itself is imported straight from `app/src/lib/supabase.ts`
 * (see `App.tsx`), so there is exactly one source of truth for how it is
 * configured.
 *
 * Password only — no passkey ceremony. This console is opened rarely, from
 * a desktop, and the ceremony is enough code that duplicating it for a
 * screen Doug reaches for a few times a month is not a good trade. The
 * session itself is not shared with app.schnauzelist.com either way:
 * different origin, different localStorage.
 *
 * U0.3: signing in is no longer the same as being allowed in. Since U0.2 the
 * database refuses every write here to a user who is not in `app_admins`, so
 * `adminState` asks the same table the app's `useIsAdmin` does (a user may
 * select their own row) and the console draws a refusal rather than a screen
 * whose every save would 403. `checking` and `error` both draw as not-yet,
 * never as admin. Plain supabase-js again, for the reason in the first
 * paragraph: this file does not import the app's hook.
 */
import { useEffect, useState } from 'react'
import type { Session, SupabaseClient } from '@supabase/supabase-js'

export type AdminState = 'checking' | 'admin' | 'refused' | 'error'

export function useSession(supabase: SupabaseClient) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [checked, setChecked] = useState<{ uid: string; state: Exclude<AdminState, 'checking'> } | null>(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => sub.subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keyed by user id: signing out and in as someone else must not read the
  // last person's answer, and a stale answer is `checking`, not `admin`.
  const uid = session?.user?.id
  useEffect(() => {
    if (!uid) return
    let live = true
    supabase.from('app_admins').select('user_id').eq('user_id', uid).maybeSingle()
      .then(({ data, error }) => {
        if (!live) return
        setChecked({ uid, state: error ? 'error' : data ? 'admin' : 'refused' })
      })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])
  const adminState: AdminState = !uid || checked?.uid !== uid ? 'checking' : checked.state

  return {
    session,
    loading,
    adminState,
    async signIn(email: string, password: string) {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      return error?.message ?? null
    },
    async signOut() {
      await supabase.auth.signOut()
    },
  }
}
